// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiUser } from '../../types/accounts';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import {
  ProfileTab,
  FriendsTab,
  GroupsTab,
  NotificationsTab,
  WalletTab,
  LeaderboardTab
} from './index';
import './AccountDetail.scss';

// SVG Icons
const ExportIcon = () => (
  <img src="/static/svg/export.svg" alt="" width="13" height="13" className="me-2" />
);

const BanIcon = () => (
  <img src="/static/svg/ban.svg" alt="" width="13" height="13" className="me-2" />
);

const DeleteIcon = () => (
  <img src="/static/svg/bin-red.svg" alt="" width="14" height="14" className="me-2" />
);

const RecordedDeleteIcon = () => (
  <img src="/static/svg/bin-recorded.svg" alt="" width="15" height="15" className="me-2" />
);

const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="me-2" />
);

interface TabView {
  label: string;
  path: string;
}

const AccountDetail: React.FC = () => {
  const { userId, tab } = useParams<{ userId: string; tab?: string }>();
  const navigate = useNavigate();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [account, setAccount] = useState<ApiUser | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const sessionRole = authService.getSessionRole();

  const views: TabView[] = [
    { label: 'Profile', path: 'profile' },
    { label: 'Wallet', path: 'wallet' },
    { label: 'Leaderboards', path: 'leaderboards' },
    { label: 'Notifications', path: 'notifications' },
  ];

  useEffect(() => {
    if (userId) {
      loadAccount();
    }
  }, [userId]);

  // Add keyboard navigation for tabs
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!account || !userId) return;
      
      // Only handle keyboard shortcuts when not in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTabIndex = views.findIndex(view => view.path === (tab || 'profile'));
      
      if (event.altKey) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            if (currentTabIndex > 0) {
              navigate(`/accounts/${userId}/${views[currentTabIndex - 1].path}`);
            }
            break;
          case 'ArrowRight':
            event.preventDefault();
            if (currentTabIndex < views.length - 1) {
              navigate(`/accounts/${userId}/${views[currentTabIndex + 1].path}`);
            }
            break;
          case '1':
          case '2':
          case '3':
          case '4':
          case '5':
          case '6':
          case '7':
          case '8':
            event.preventDefault();
            const tabIndex = parseInt(event.key) - 1;
            if (tabIndex < views.length) {
              navigate(`/accounts/${userId}/${views[tabIndex].path}`);
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [account, userId, tab, navigate, views]);

  // Redirect to profile tab if no tab is specified
  useEffect(() => {
    if (userId && (!tab || tab === '')) {
      navigate(`/accounts/${userId}/profile`, { replace: true });
    }
  }, [userId, tab, navigate]);

  const loadAccount = async () => {
    if (!userId) return;

    setLoading(true);
    setError('');

    try {
      const accountData = await consoleService.getAccount(userId);
      // Nakama API returns: { account: { user: {...}, wallet: "...", disable_time: "..." } }
      // We need to merge wallet and disable_time into the user object
      const user = accountData.account?.user || accountData.user || accountData;
      const wallet = accountData.account?.wallet || accountData.wallet || '{}';
      const disable_time = accountData.account?.disable_time || accountData.disable_time || null;
      
      // Merge wallet and disable_time into user object
      setAccount({ ...user, wallet, disable_time });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAccount = async () => {
    if (!account) return;

    try {
      // This would need to be implemented in the console service
      // const accountExport = await consoleService.exportAccount('', account.id);
      // const fileName = `${account.id}-export.json`;
      // const json = JSON.stringify(accountExport, null, 2);
      // const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
      // saveAs(blob, fileName);
      console.log('Export functionality not yet implemented');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to export account');
    }
  };

  const handleBanUnbanAccount = async () => {
    if (!account) return;

    try {
      if (account.disable_time != null) {
        await consoleService.unbanAccount('', account.id);
        setAccount({ ...account, disable_time: null });
      } else {
        await consoleService.banAccount('', account.id);
        setAccount({ ...account, disable_time: new Date().toISOString() });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to ban/unban account');
    }
  };

  const handleDeleteAccount = (recordDeletion: boolean) => {
    if (!account) return;

    const action = recordDeletion ? 'recorded delete' : 'delete';
    openConfirmDialog(
      `${recordDeletion ? 'Recorded Delete' : 'Delete'} Account`,
      `Are you sure you want to ${action} the account for user "${account.username || account.id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteAccount('', account.id, recordDeletion);
          navigate('/accounts');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete account');
        }
      }
    );
  };

  const exportAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const banAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const renderTabContent = () => {
    if (!account) return null;
    
    const currentTab = tab || 'profile';
    
    switch (currentTab) {
      case 'profile':
        return <ProfileTab account={account} onAccountUpdate={setAccount} />;
      case 'friends':
        return <FriendsTab account={account} />;
      case 'groups':
        return <GroupsTab account={account} />;
      case 'notifications':
        return <NotificationsTab account={account} />;
      case 'wallet':
        return <WalletTab account={account} />;
      case 'leaderboards':
        return <LeaderboardTab account={account} />;
      default:
        return <ProfileTab account={account} onAccountUpdate={setAccount} />;
    }
  };

  if (loading) {
    return (
      <div className="account-detail-container">
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading account...</strong>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="account-detail-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Account not found</strong>
        </div>
      </div>
    );
  }

  const getUserStatus = () => {
    // Check if this is the system user
    
    if (account.id === '00000000-0000-0000-0000-000000000000') {
      return { text: 'System', color: '#6f42c1', icon: '⚙️' };
    }
    
    if (account.disable_time) {
      return { text: 'Banned', color: '#dc3545', icon: '🚫' };
    }
    // You could add online/offline logic here based on last_online_time
    const lastOnline = account.update_time ? new Date(account.update_time) : null;
    const now = new Date();
    const hoursSinceUpdate = lastOnline ? (now.getTime() - lastOnline.getTime()) / (1000 * 60 * 60) : null;
    
    if (hoursSinceUpdate && hoursSinceUpdate < 1) {
      return { text: 'Online', color: '#28a745', icon: '🟢' };
    } else if (hoursSinceUpdate && hoursSinceUpdate < 24) {
      return { text: 'Recently Active', color: '#ffc107', icon: '🟡' };
    } else {
      return { text: 'Offline', color: '#6c757d', icon: '⚫' };
    }
  };

  const formatJoinDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const status = getUserStatus();

  return (
    <div className="account-detail-container-clean">
      {/* Clean Profile Header */}
      <div className="player-header-clean">
        <div className="header-left">
          <div className="player-info">
            <h1 className="player-name">
              {account.id === '00000000-0000-0000-0000-000000000000' 
                ? 'System User' 
                : (account.display_name || account.username || 'Unnamed User')
              }
            </h1>
            <div className="player-meta">
              <span className="meta-item">
                <span className="meta-label">@</span>
                {account.username || 'no-username'}
              </span>
              <span className="meta-divider">•</span>
              <span className="meta-item status" style={{ color: status.color }}>
                {status.text}
              </span>
              <span className="meta-divider">•</span>
              <span className="meta-item">
                Joined {formatJoinDate(account.create_time)}
              </span>
            </div>
            <div className="player-id">
              <code>{account.id}</code>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {banAllowed() && (
            <button
              onClick={handleBanUnbanAccount}
              type="button"
              className={`action-btn ${account.disable_time != null ? 'btn-unban' : 'btn-ban'}`}
            >
              {account.disable_time != null ? '✓ Unban' : '⊘ Ban'}
            </button>
          )}
          {deleteAllowed() && (
            <button
              onClick={() => handleDeleteAccount(false)}
              type="button"
              className="action-btn btn-delete"
            >
              🗑 Delete
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <RedTriangleIcon />
          <h6 className="d-inline fw-bold mb-0">An error occurred: {error}</h6>
        </div>
      )}

      <nav className="player-tabs-clean">
        {views.map((view, index) => {
          const icons = ['👤', '💰', '🏆', '🔔'];
          return (
            <Link
              key={view.path}
              to={`/accounts/${userId}/${view.path}`}
              className={`tab-item ${tab === view.path ? 'active' : ''}`}
            >
              <span className="tab-icon">{icons[index]}</span>
              {view.label}
            </Link>
          );
        })}
      </nav>

      <div className="tab-content-clean">
        {renderTabContent()}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default AccountDetail;
